/**
 * @deprecated Legacy tokens, kept only for the styled-components files that still
 * read them (`theme.colors.*`, `space()`, `radius()`, `fontSize()`). The source of
 * truth is the antd theme in `src/config/antdTheme.js`; styled-components reads it
 * as `theme.token.*` through `AntdTokenBridge`. Do not add values here and do not
 * import this from new code — it shrinks as files are touched, with no whole-repo
 * sweep. The px-vs-rem reasoning below is recorded on issue #577.
 *
 * The design system's token values — colours, spacing and radius ramps, the
 * type scale and z-index layers.
 *
 * Deliberately a plain module with **no imports**: `Theme.context.jsx` needs
 * styled-components, which makes it unreadable to anything outside the React
 * runtime. Tooling that reads these values without React — the design-system
 * ESLint rules and the `/design-system` gallery, where a branch carries them,
 * plus tests — needs them here, and `Theme.context.jsx` builds its theme
 * object from them. Keep it import-free.
 *
 * Plain Node has to be able to import this, not just Vite: the ESLint
 * design-system rules run under Node and read the token values from here. That
 * works because package.json declares `"type": "module"` — without it Node
 * treats every `.js` file under `src/` as CommonJS and rejects the `export`
 * below, while Vite carries on regardless. If that field ever goes away, the
 * lint rules stop being able to load their own token set.
 *
 * Hand-maintained, not generated. The values were derived from a one-off audit
 * of the running app in Aug 2026; that tooling was deleted 1 Sep 2026, so this
 * file is now the source of truth outright. Every change here is deliberate.
 */

const navHeight = '62px';

const utils = {
	fullHeight: `calc(100vh - ${navHeight})`,
	sidebarWidth: '340px',
	navHeight,
};

export const themeTokens = {
	colors: {
		darkGreen: '#239442',
		green: '#20AC6B',
		lightGreen: '#90d7ad',
		textColor: '#cfcfcf',
		textAccent: '#B0B0B0',
		linkColor: '#1BAC6A',
		linkColorRgb: '27, 172, 106',
		background: '#1c1c1c',
		backgroundRgb: '28, 28, 28',
		backgroundAccent: '#353535',
		backgroundAccentMuted: '#212529',
		backgroundInput: '#171717',
		backgroundGrey: '#343a40',
		modal: '#171717',
		border: '#262626',
		borderLight: '#989898',
		borderLightRgb: '152, 152, 152',
		borderInput: '#353535',
		warning: '#e23838',
		/** Semi-transparent panel background RGB value for glassmorphism effect */
		panelBackgroundRgb: '25, 25, 25',

		// Named 18 Aug 2026 from the audit's overrides — colours painted
		// hundreds of times across the app
		// but never declared here, so they had no name to import instead of the
		// raw hex. Values are unchanged; this only gives existing usage a name.
		textSecondary: '#ffffff',
		textMuted: '#cccccc',
		borderDefault: '#495057',
		borderStrong: '#198754',
		surfaceOverlay: '#414141',
		shadowColor: '#000000',
	},
	// Spacing/radius ramps and the type scale, derived the same way as the
	// colours above: from what the app already painted, not from preference.
	// Unitless; `space()` below adds `px` for spacing,
	// other consumers add their own unit.
	//
	// Spacing is **px**, deliberately, and the counterpart to the type scale's
	// `rem` below — settled 25 Aug 2026 the same way, by following what the app
	// already did: it wrote spacing in px 446 times against 159 in rem, and font
	// sizes in rem 131 times against 8 in px. The 159 rem sites were converted
	// rather than the ramp changed.
	//
	// So a reader who raises their browser's default font size gets larger text
	// on a steady layout, rather than both growing together. That is the common
	// pairing, and it is a choice: fully-rem spacing would scale padding too.
	// Page zoom — the far more common mechanism — scales px regardless, so this
	// only affects the font-size-only setting.
	spacing: [0, 2, 4, 6, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80],
	radius: [0, 2, 4, 5, 8, 999],
	typography: {
		/**
		 * Type sizes in **rem**, unlike the spacing and radius ramps, which are
		 * px. Decided 20 Aug 2026, following how the app already writes them: 131
		 * of its 139 font-size literals are `rem` and every one lands exactly on a
		 * step here — the scale was derived from them. `rem` is relative to the
		 * root, so these respect a reader who has raised their browser's default
		 * font size; a px scale would silently ignore that.
		 *
		 * The px equivalents at the default 16px root are 12, 12.8, 14, 14.4, 16,
		 * 19.2, 24, 28.
		 */
		size: {
			'3xs': 0.75,
			'2xs': 0.8,
			xs: 0.875,
			sm: 0.9,
			base: 1,
			lg: 1.2,
			xl: 1.5,
			'2xl': 1.75,
		},
		// Named, not indexed: as an array, index 3 was 300 and index 4 was 700, so
		// a call site said nothing and the natural guess (higher index, heavier)
		// was wrong. Values are the five weights the app already used.
		weights: { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700 },
		fontFamily: 'Montserrat, sans-serif',
	},
	layers: {
		base: 0,
		raised: 1,
		sticky: 2,
		dropdown: 100,
		overlay: 1090,
		modal: 9999,
	},
	utils: { ...utils },
};

/**
 * Type-scale name → a rem string: `fontSize('base')` is `'1rem'`.
 *
 * Named steps rather than indices, because a type scale is read by role — `base`,
 * `lg` — not by position, and `fontSize('lg')` survives a step being inserted
 * below it where `size[5]` would not.
 */
export const fontSize = step => {
	const value = themeTokens.typography.size[step];
	if (value === undefined)
		throw new Error(
			`fontSize(${JSON.stringify(step)}): not a type-scale step — use one of ${Object.keys(
				themeTokens.typography.size
			).join(', ')}`
		);
	return `${value}rem`;
};

/**
 * Radius-ramp index → a px string: `radius(2)` is `'4px'`, `radius(5)` the pill.
 *
 * Same shape and the same reasoning as `space()` below — the ramp is unitless,
 * so without this every call site repeats `}px`. Kept as its own function rather
 * than one `scale(ramp, i)` helper because the call sites read better naming the
 * ramp they mean, and a wrong ramp would then be a silent bug rather than an
 * unresolvable import.
 */
export const radius = step => {
	const value = themeTokens.radius[step];
	if (value === undefined)
		throw new Error(
			`radius(${step}): not a radius-ramp index — use 0-${themeTokens.radius.length - 1}`
		);
	return `${value}px`;
};

/**
 * Spacing-ramp index → a px string: `space(6)` is `'16px'`.
 *
 * The ramp is stored unitless so each family decides its own unit, which left
 * every spacing call site repeating `}px`. Extra arguments build a CSS
 * shorthand — `space(2, 5)` is `'4px 12px'` — because 133 of the spacing
 * declarations in `src/` are two-to-four-value shorthands.
 *
 * Throws on an index that isn't on the ramp. The alternative is emitting
 * `'undefinedpx'`: invalid CSS, silently dropped by the browser, leaving a
 * subtly wrong layout and no error anywhere — the exact drift the token set
 * exists to prevent.
 */
export const space = (...steps) => {
	if (!steps.length) throw new Error('space() needs at least one spacing-ramp index');

	const lastStep = themeTokens.spacing.length - 1;

	return steps
		.map(step => {
			const value = themeTokens.spacing[step];
			if (value === undefined)
				throw new Error(`space(${step}): not a spacing-ramp index — use 0-${lastStep}`);
			return `${value}px`;
		})
		.join(' ');
};
